import React, { useEffect, useState, useRef } from "react";
import { useSuiClient, useSignAndExecuteTransaction } from "@mysten/dapp-kit";
import { toast } from "sonner";
import { Zap, ChevronUp, ChevronDown } from "lucide-react";
import { useWallet, useWalletBalance } from "@/context/WalletContext";
import { useDeployment } from "@/context/DeploymentContext";
import { buyOnCurve, sellOnCurve, getTokenBalance } from "@/lib/sui";
import { api } from "@/lib/api";
import { fmtNum, fmtSui } from "@/lib/format";
import { isTouch } from "@/lib/perf";

const PRESETS = [10, 50, 100, 250];

export default function OnChainTradePanel({ coin, onTraded, initialSide }) {
  const { connected, address, refetchReal } = useWallet();
  const { realBalance } = useWalletBalance();
  const { deployment } = useDeployment();
  const client = useSuiClient();
  const { mutateAsync: signExec } = useSignAndExecuteTransaction();
  const [side, setSide] = useState(initialSide || "buy");
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState(3);
  const [quote, setQuote] = useState(null);
  const [tokenBal, setTokenBal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [stepMsg, setStepMsg] = useState("");

  const dec = coin.decimals ?? 9;
  const scale = 10 ** dec;
  const pkg = deployment?.package_id;
  const configId = deployment?.config_id;

  const loadBalInflightRef = useRef(false);
  const loadBal = async () => {
    if (!address || loadBalInflightRef.current) return;
    loadBalInflightRef.current = true;
    try {
      const b = await getTokenBalance(client, { owner: address, coinType: coin.coin_type });
      setTokenBal(Number(b) / scale);
    } catch (e) {} finally { loadBalInflightRef.current = false; }
  };
  useEffect(() => {
    if (!address) return;
    const t = setTimeout(loadBal, 250);
    return () => clearTimeout(t);
  }, [address, coin.coin_type]); // eslint-disable-line

  useEffect(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || !coin.raw) { setQuote(null); return; }
    try {
      const V = BigInt(coin.raw.virtual_sui_mist), R = BigInt(coin.raw.real_sui_mist);
      const TR = BigInt(coin.raw.token_reserve_base);
      const VT = BigInt(coin.raw.virtual_token_base || coin.raw.token_supply_base);
      const AVAIL = BigInt(coin.raw.token_balance_base || TR);
      const k = V * VT, suiEff = V + R;
      if (side === "buy") {
        const amtMist = BigInt(Math.floor(amt * 1e9));
        const net = (amtMist * 99n) / 100n;
        let out = TR - k / (suiEff + net);
        let capped = false;
        if (out >= AVAIL) { out = AVAIL; capped = true; }
        setQuote({ out: Number(out) / scale, dev: amt * 0.007, plat: amt * 0.003, capped });
      } else {
        const inBase = BigInt(Math.floor(amt * scale));
        const gross = Number(suiEff - k / (TR + inBase)) / 1e9;
        setQuote({ out: gross * 0.99, dev: gross * 0.007, plat: gross * 0.003, capped: false });
      }
    } catch (e) { setQuote(null); }
  }, [amount, side, coin.decimals, coin.migrated,
      coin.raw?.virtual_sui_mist, coin.raw?.real_sui_mist, coin.raw?.token_reserve_base,
      coin.raw?.virtual_token_base, coin.raw?.token_supply_base, coin.raw?.token_balance_base]);

  const bumpAmount = (dir) => {
    const cur = parseFloat(amount) || 0;
    const step = side === "buy" ? 1 : (tokenBal > 0 ? tokenBal / 100 : 1000);
    let next = cur + dir * step;
    if (next < 0) next = 0;
    if (side === "sell" && next > tokenBal * 0.9999) next = tokenBal * 0.9999;
    const rounded = side === "buy" ? parseFloat(next.toFixed(4)) : Math.round(next);
    setAmount(String(rounded));
  };

  const doTrade = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    if (!connected) return toast.error("Connect your Sui wallet first");
    if (!pkg) return toast.error("Launchpad not deployed");
    setBusy(true);
    try {
      if (side === "buy") {
        const minOut = quote ? BigInt(Math.floor(quote.out * scale * (1 - slippage / 100))) : 0n;
        setStepMsg("Confirm in wallet…");
        const digest = await buyOnCurve(signExec, client, { pkg, configId, curveId: coin.curve_id, coinType: coin.coin_type, amountMist: BigInt(Math.floor(amt * 1e9)), minOut });
        api.post("/referral/track", { digest, trader: address }).catch(() => {});
        api.post("/points/track", { digest, trader: address }).catch(() => {});
        toast.success(`Bought $${coin.symbol} · ${digest.slice(0, 8)}…`);
      } else {
        const minSui = quote ? BigInt(Math.floor(quote.out * 1e9 * (1 - slippage / 100))) : 0n;
        setStepMsg("Confirm in wallet…");
        const digest = await sellOnCurve(signExec, client, { pkg, configId, curveId: coin.curve_id, coinType: coin.coin_type, amountBase: BigInt(Math.floor(amt * scale)), minSuiOut: minSui }, address);
        api.post("/referral/track", { digest, trader: address }).catch(() => {});
        api.post("/points/track", { digest, trader: address }).catch(() => {});
        toast.success(`Sold $${coin.symbol} · ${digest.slice(0, 8)}…`);
      }
      setAmount(""); setQuote(null);
      // Mobile: hard-refresh shortly after a confirmed trade so balances/curve repaint reliably
      // inside the Slush webview. Scheduled BEFORE the post-trade refetch so a flaky RPC there
      // can never cancel it.
      if (isTouch) setTimeout(() => { try { window.location.reload(); } catch (e) {} }, 1500);
      try { await Promise.all([loadBal(), refetchReal?.()]); } catch (e) {}
      onTraded && onTraded();
    } catch (err) {
      console.error(err);
      toast.error((err?.message || "Trade failed").slice(0, 140));
    } finally { setBusy(false); setStepMsg(""); }
  };

  if (coin.migrated) {
    const cetus = `https://app.cetus.zone/swap?from=0x2::sui::SUI&to=${coin.coin_type}`;
    return (
      <div className="card-surface p-6 text-center" data-testid="onchain-trade-panel">
        <div className="text-base font-display font-extrabold mb-1">Graduated to Cetus 🎉</div>
        <p className="text-xs text-muted-foreground mb-4">The bonding curve is complete. This token now trades on the Cetus DEX.</p>
        <a href={cetus} target="_blank" rel="noreferrer" data-testid="trade-on-cetus-link" className="inline-block w-full py-3 rounded-xl font-display font-bold text-black" style={{ background: "linear-gradient(90deg,#00E5FF,#1EC62E)" }}>Trade on Cetus →</a>
      </div>
    );
  }
  if ((coin.progress ?? 0) >= 0.999) {
    return (
      <div className="card-surface p-6 text-center" data-testid="onchain-trade-panel">
        <div className="text-base font-display font-extrabold mb-1">Graduating to Cetus…</div>
        <p className="text-xs text-muted-foreground">The bonding curve is full. Buying and selling are paused while liquidity is added to Cetus and the LP is locked. This usually takes about a minute — the page will update automatically.</p>
      </div>
    );
  }

  const buyGrad = "linear-gradient(90deg,#16C784,#00E0A3)";
  const sellGrad = "linear-gradient(90deg,#F43F5E,#FF007A)";

  return (
    <div className="card-surface p-4 sm:p-5" data-testid="onchain-trade-panel">
      <div className="flex items-center gap-1.5 mb-4 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        <Zap className="w-3.5 h-3.5 text-[#1EC62E]" /> Trade ${coin.symbol}
      </div>

      <div className="grid grid-cols-2 gap-1.5 p-1 bg-black/30 rounded-xl border border-white/10 mb-4">
        <button data-testid="trade-tab-buy" onClick={() => { setSide("buy"); setAmount(""); setQuote(null); }} className={`py-2.5 rounded-lg font-bold text-sm transition-all ${side === "buy" ? "text-black" : "text-muted-foreground hover:text-white"}`} style={side === "buy" ? { background: buyGrad } : {}}>Buy</button>
        <button data-testid="trade-tab-sell" onClick={() => { setSide("sell"); setAmount(""); setQuote(null); }} className={`py-2.5 rounded-lg font-bold text-sm transition-all ${side === "sell" ? "text-white" : "text-muted-foreground hover:text-white"}`} style={side === "sell" ? { background: sellGrad } : {}}>Sell</button>
      </div>

      <div className="flex items-center justify-between mb-1.5 text-xs">
        <span className="text-muted-foreground">Amount ({side === "buy" ? "SUI" : coin.symbol})</span>
        <button onClick={() => { if (side === "sell") setAmount(String(tokenBal * 0.9999)); }} className="font-mono text-muted-foreground hover:text-[#1EC62E] transition-colors">
          {side === "buy" ? `${fmtSui(realBalance, 3)} SUI` : `${fmtNum(tokenBal)} ${coin.symbol}`}
        </button>
      </div>
      <div className="relative">
        <input data-testid="sui-amount-input" type="number" step="any" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="no-spinner w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-24 py-3.5 text-xl font-mono font-bold focus:outline-none focus:border-[#1EC62E]/60 transition-colors" />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2.5">
          <span className="text-xs font-mono text-muted-foreground">{side === "buy" ? "SUI" : `$${coin.symbol}`}</span>
          <div className="qty-stepper">
            <button type="button" data-testid="amount-step-up" aria-label="Increase amount" onClick={() => bumpAmount(1)} className="qty-step-btn qty-step-up">
              <ChevronUp className="w-3.5 h-3.5" strokeWidth={3} />
            </button>
            <button type="button" data-testid="amount-step-down" aria-label="Decrease amount" onClick={() => bumpAmount(-1)} className="qty-step-btn qty-step-down">
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5 mt-2.5">
        {side === "buy"
          ? PRESETS.map((p) => <button key={p} onClick={() => setAmount(String(p))} className="py-2 rounded-lg bg-white/5 border border-white/10 text-xs font-mono hover:border-[#1EC62E]/50 hover:text-[#1EC62E] transition-colors">{p}</button>)
          : [25, 50, 75, 100].map((p) => <button key={p} onClick={() => setAmount(String(p >= 100 ? tokenBal * 0.9999 : (tokenBal * p) / 100))} className="py-2 rounded-lg bg-white/5 border border-white/10 text-xs font-mono hover:border-[#F43F5E]/50 hover:text-[#F43F5E] transition-colors">{p === 100 ? "MAX" : `${p}%`}</button>)}
      </div>

      <div className="flex items-center gap-2 mt-3.5 text-xs">
        <span className="text-muted-foreground">Slippage</span>
        {[1, 3, 5].map((s) => <button key={s} onClick={() => setSlippage(s)} className={`px-2.5 py-1 rounded-lg font-mono border transition-colors ${slippage === s ? "border-[#1EC62E]/60 text-[#1EC62E] bg-[#1EC62E]/5" : "border-white/10 text-muted-foreground hover:text-white"}`}>{s}%</button>)}
      </div>

      {quote && (
        <div className="mt-4 space-y-1.5 text-xs font-mono bg-black/30 border border-white/10 rounded-xl p-3.5">
          <div className="flex justify-between"><span className="text-muted-foreground">You receive</span><span className="font-bold text-sm">{side === "buy" ? `${fmtNum(quote.out)} ${coin.symbol}` : `${fmtSui(quote.out, 4)} SUI`}</span></div>
          {quote.capped && side === "buy" && (
            <div data-testid="buy-caps-graduation-note" className="text-[10px] text-[#1EC62E] leading-snug pt-1">This buys all remaining tokens → graduates the coin. Any extra SUI is refunded automatically.</div>
          )}
          <div className="flex justify-between text-[11px] pt-1 border-t border-white/5 mt-1"><span className="text-muted-foreground">Dev fee 0.7%</span><span className="text-[#1EC62E]">{fmtSui(quote.dev, 4)}</span></div>
          <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Platform 0.3%</span><span className="text-[#1EC62E]">{fmtSui(quote.plat, 4)}</span></div>
        </div>
      )}

      <button onClick={doTrade} disabled={busy} data-testid="submit-trade-button" className="w-full mt-4 py-3.5 rounded-xl font-display font-bold text-base transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50" style={{ background: side === "buy" ? buyGrad : sellGrad, color: side === "buy" ? "#04121f" : "#fff" }}>
        {busy ? (stepMsg || "Processing…") : `${side === "buy" ? "BUY" : "SELL"} $${coin.symbol}`}
      </button>
      <p className="mt-2.5 text-[10px] text-center text-muted-foreground">Live on Sui mainnet · signed in your wallet · gas paid by you</p>
    </div>
  );
}
